import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import AutoFixHighOutlined from '@mui/icons-material/AutoFixHighOutlined';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import LockClockIcon from '@mui/icons-material/LockClock';
import TimelapseIcon from '@mui/icons-material/Timelapse';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { APP_ROUTES } from 'constants/routes';
import { values } from 'lodash';
import {
	ArchType,
	IWizardAuthMethodItem,
	IWizardChecklistSection,
	IWizardCombination,
	WizardAuthMethod
} from 'types/wizard';

const SSO_METHOD: IWizardAuthMethodItem = {
	value: WizardAuthMethod.SSO,
	icon: VpnKeyIcon,
	subtitle: true,
	label: 'pages.authMethods.sso.title',
	disabledOnSecondary: true
};

const BIOMETRICS_METHOD: IWizardAuthMethodItem = {
	value: WizardAuthMethod.Biometrics,
	icon: FingerprintIcon,
	subtitle: false,
	label: 'pages.authMethods.webauthn.title'
};

const SOCIALS_METHOD: IWizardAuthMethodItem = {
	value: WizardAuthMethod.Socials,
	icon: AssignmentIndIcon,
	subtitle: true,
	label: 'pages.authMethods.social.title',
	disabledOnSecondary: true
};

const MAGIC_LINK_METHOD: IWizardAuthMethodItem = {
	value: WizardAuthMethod.MagicLink,
	icon: AutoAwesome,
	subtitle: false,
	label: 'pages.authMethods.magiclink.title'
};

const ENCHANTED_LINK_METHOD: IWizardAuthMethodItem = {
	value: WizardAuthMethod.EnchantedLink,
	icon: AutoFixHighOutlined,
	subtitle: false,
	label: 'pages.authMethods.enchantedlink.title'
};

const OTP_METHOD: IWizardAuthMethodItem = {
	value: WizardAuthMethod.OTP,
	icon: LockClockIcon,
	subtitle: false,
	label: 'pages.authMethods.otp.title'
};

const TOTP_METHOD: IWizardAuthMethodItem = {
	value: WizardAuthMethod.TOTP,
	icon: TimelapseIcon,
	subtitle: false,
	label: 'pages.authMethods.totp.title'
};

export const getWizardAuthMethodItemLabel = (
	method?: WizardAuthMethod
): string => {
	switch (method) {
		case WizardAuthMethod.SSO:
			return SSO_METHOD.label;
		case WizardAuthMethod.Biometrics:
			return BIOMETRICS_METHOD.label;
		case WizardAuthMethod.Socials:
			return SOCIALS_METHOD.label;
		case WizardAuthMethod.MagicLink:
			return MAGIC_LINK_METHOD.label;
		case WizardAuthMethod.EnchantedLink:
			return ENCHANTED_LINK_METHOD.label;
		case WizardAuthMethod.OTP:
			return OTP_METHOD.label;
		case WizardAuthMethod.TOTP:
			return TOTP_METHOD.label;
		default:
			return 'wizard.phase.build.auth.method.none';
	}
};

export const NONE_AUTH_METHOD = 'none';

export const PRIMARY_METHODS_BTC = [
	BIOMETRICS_METHOD,
	SOCIALS_METHOD,
	OTP_METHOD,
	MAGIC_LINK_METHOD,
	ENCHANTED_LINK_METHOD,
	TOTP_METHOD,
	SSO_METHOD
];

export const PRIMARY_METHODS_BTB = [
	SSO_METHOD,
	MAGIC_LINK_METHOD,
	ENCHANTED_LINK_METHOD,
	BIOMETRICS_METHOD,
	SOCIALS_METHOD,
	OTP_METHOD,
	TOTP_METHOD
];

export const WIZARD_PAGES = {
	// build
	archType: 'archType',
	primaryMethods: 'primaryMethods',
	secondaryMethod: 'secondaryMethod',
	suggestions: 'suggestions',
	apply: 'apply',
	// integrate
	preview: 'preview',
	// customize
	checklist: 'checklist'
};

export const WIZARD_CHECKLIST_SECTION = {
	customize: 'customize',
	initialSetup: 'initialSetup',
	share: 'share'
};

export const WIZARD_CHECKLIST_CUSTOMIZE_ITEMS = {
	project: 'project',
	styling: 'styling',
	flows: 'flows'
};

export const WIZARD_CHECKLIST_INITIAL_SETUP_ITEMS = {
	sessionTokens: 'sessionTokens',
	signUp: 'signUp',
	redirectURL: 'redirectURL',
	configureOAuthProviders: 'configureOAuthProviders'
};

export const WIZARD_CHECKLIST_SHARE_ITEMS_MT = {
	rolesPermissions: 'rolesPermissions',
	tenant: 'tenant',
	inviteMT: 'inviteMT'
};

export const WIZARD_CHECKLIST_SHARE_ITEMS_B2C = {
	rolesPermissions: 'rolesPermissions',
	inviteB2C: 'inviteB2C'
};

export const getWizardCheckListItems = (
	name: string,
	archType: `${ArchType}` | ''
): string[] => {
	let itemsMap = {};
	switch (name) {
		case WIZARD_CHECKLIST_SECTION.customize:
			itemsMap = WIZARD_CHECKLIST_CUSTOMIZE_ITEMS;
			break;
		case WIZARD_CHECKLIST_SECTION.initialSetup:
			itemsMap = WIZARD_CHECKLIST_INITIAL_SETUP_ITEMS;
			break;
		case WIZARD_CHECKLIST_SECTION.share:
			itemsMap =
				archType === ArchType.B2C
					? WIZARD_CHECKLIST_SHARE_ITEMS_B2C
					: WIZARD_CHECKLIST_SHARE_ITEMS_MT;
			break;
		default:
			return [];
	}

	return values(itemsMap);
};

export const getWizardDefaultSection = (
	name: string,
	archType: `${ArchType}` | ''
): IWizardChecklistSection => {
	const items = getWizardCheckListItems(name, archType) || [];
	return {
		name,
		items: items.map((item: string) => ({
			status: '',
			name: item
		}))
	};
};

export const isWizardSSOSelected = (
	combinations: IWizardCombination[]
): boolean =>
	combinations.some(
		(combination: IWizardCombination) =>
			combination.selected &&
			(combination.secondaryMethod === WizardAuthMethod.SSO ||
				combination.primaryMethods.includes(WizardAuthMethod.SSO))
	);

export const WIZARD_CHECKLIST_ITEM_DONE = 'done';

export const CHECKLIST_LINKS = {
	// customize
	[WIZARD_CHECKLIST_CUSTOMIZE_ITEMS.project]: APP_ROUTES.projectSettings,
	[WIZARD_CHECKLIST_CUSTOMIZE_ITEMS.styling]: APP_ROUTES.flowStyleEditor,
	[WIZARD_CHECKLIST_CUSTOMIZE_ITEMS.flows]: APP_ROUTES.flows,
	// setup
	[WIZARD_CHECKLIST_INITIAL_SETUP_ITEMS.sessionTokens]:
		APP_ROUTES.projectSettings,
	[WIZARD_CHECKLIST_INITIAL_SETUP_ITEMS.signUp]: APP_ROUTES.projectSettings,
	[WIZARD_CHECKLIST_INITIAL_SETUP_ITEMS.redirectURL]:
		APP_ROUTES.projectSettings,
	[WIZARD_CHECKLIST_INITIAL_SETUP_ITEMS.configureOAuthProviders]:
		APP_ROUTES.authMethods,
	// share mt
	[WIZARD_CHECKLIST_SHARE_ITEMS_MT.rolesPermissions]: APP_ROUTES.authorization,
	[WIZARD_CHECKLIST_SHARE_ITEMS_MT.tenant]: APP_ROUTES.tenants,
	[WIZARD_CHECKLIST_SHARE_ITEMS_MT.inviteMT]: APP_ROUTES.users,
	// share b2c
	[WIZARD_CHECKLIST_SHARE_ITEMS_B2C.rolesPermissions]: APP_ROUTES.authorization,
	[WIZARD_CHECKLIST_SHARE_ITEMS_B2C.inviteB2C]: APP_ROUTES.users
};
